import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Clock, GripVertical, ChevronDown, ChevronRight, FolderPlus, Upload, ArrowLeft, MapPin, Users, Settings, Check } from 'lucide-react';
import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable category block — uses dnd-kit so it works on iPad + Mac
function SortableCategoryBlock({ cat, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `cat-${cat.id}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.12)' : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="border border-[#E5E7E1] rounded-xl bg-white transition-all" data-testid={`cat-group-${cat.name}`}>
      {typeof children === 'function' ? children({ dragAttributes: attributes, dragListeners: listeners }) : children}
    </div>
  );
}

// Sortable row using dnd-kit (works on touch + mouse + keyboard)
function SortableServiceRow({ s, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border border-[#E5E7E1] bg-white hover:border-[#556B2F]/30 transition-colors ${!s.is_active ? 'opacity-50' : ''}`}
      data-testid={`service-card-${s.id}`}>
      <button type="button" {...attributes} {...listeners}
        className="touch-none cursor-grab active:cursor-grabbing p-1 -ml-1 text-[#B0B5A8] hover:text-[#556B2F]"
        aria-label="Drag to reorder"
        data-testid={`service-drag-handle-${s.id}`}>
        <GripVertical size={16} />
      </button>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-[#1A2421]">{s.name}</p>
        {s.description && <p className="text-xs text-[#5C6656] truncate mt-0.5">{s.description}</p>}
        <span className="text-xs text-[#5C6656] flex items-center gap-1 mt-0.5"><Clock size={11} /> {s.duration}min</span>
      </div>
      <span className="text-sm font-semibold text-[#1A2421]">{'\u00A3'}{(s.price || 0).toFixed(2)}</span>
      <div className="flex gap-0.5 flex-shrink-0">
        <Button size="sm" variant="ghost" onClick={() => onEdit(s)} className="h-7 w-7 p-0 text-[#556B2F]"><Pencil size={13} /></Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(s.id)} className="h-7 w-7 p-0 text-[#E2725B]"><Trash2 size={13} /></Button>
      </div>
      {!s.is_active && <Badge className="bg-[#D4A373] text-white text-[10px]">Inactive</Badge>}
    </div>
  );
}

export default function ServicesPage() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', duration: 30, price: 0, category: '__uncategorised__', is_active: true });
  const [collapsedCats, setCollapsedCats] = useState({});
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catForm, setCatForm] = useState('');
  const [editCatId, setEditCatId] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverCat, setDragOverCat] = useState(null);
  const [dragCat, setDragCat] = useState(null);
  const [dragOverCatIdx, setDragOverCatIdx] = useState(null);
  const csvRef = useRef(null);
  const [importing, setImporting] = useState(false);
  // Full-page edit mode
  const [fpEdit, setFpEdit] = useState(null); // service object being edited
  const [fpTab, setFpTab] = useState('basic');
  const [fpTeam, setFpTeam] = useState([]); // team members for this service
  const [fpLocations, setFpLocations] = useState([]); // all locations
  const [fpSvcLocations, setFpSvcLocations] = useState([]); // location IDs for this service

  const fetchAll = useCallback(async () => {
    try {
      const [svcRes, catRes] = await Promise.all([
        axios.get(`${API}/services`, { withCredentials: true }),
        axios.get(`${API}/service-categories`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setServices(svcRes.data || []);
      setCategories(catRes.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, duration: parseInt(form.duration), price: parseFloat(form.price), category: form.category === '__uncategorised__' ? '' : form.category };
      if (editId) {
        await axios.put(`${API}/services/${editId}`, payload, { withCredentials: true });
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/services`, payload, { withCredentials: true });
        toast.success('Service added');
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteService = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try { await axios.delete(`${API}/services/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed'); }
  };

  const openEdit = (s) => {
    // Full-page Fresha-style edit
    setFpEdit(s);
    setFpTab('basic');
    setForm({ name: s.name, description: s.description || '', duration: s.duration, price: s.price, category: s.category || '__uncategorised__', is_active: s.is_active !== false });
    // Fetch team members and locations
    axios.get(`${API}/services/${s.id}/team-members`, { withCredentials: true }).then(r => setFpTeam(r.data)).catch(() => {});
    axios.get(`${API}/locations`, { withCredentials: true }).then(r => { setFpLocations(r.data); setFpSvcLocations(s.location_ids || []); }).catch(() => {});
  };

  const resetForm = () => { setEditId(null); setForm({ name: '', description: '', duration: 30, price: 0, category: '__uncategorised__', is_active: true }); };

  const openAddInCategory = (catName) => {
    resetForm();
    setForm(p => ({ ...p, category: catName }));
    setDialogOpen(true);
  };

  const toggleCollapse = (cat) => setCollapsedCats(p => ({ ...p, [cat]: !p[cat] }));

  // Category CRUD
  const saveCategory = async () => {
    if (!catForm.trim()) { toast.error('Name required'); return; }
    try {
      if (editCatId) {
        await axios.put(`${API}/service-categories/${editCatId}`, { name: catForm.trim() }, { withCredentials: true });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/service-categories`, { name: catForm.trim() }, { withCredentials: true });
        toast.success('Category added');
      }
      setCatDialogOpen(false); setCatForm(''); setEditCatId(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? Services in this category will become uncategorized.`)) return;
    try { await axios.delete(`${API}/service-categories/${cat.id}`, { withCredentials: true }); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed'); }
  };

  // dnd-kit sensors: pointer + touch + keyboard, works on iPad & laptop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderInCategory = async (catName, oldIdx, newIdx) => {
    const current = (grouped[catName] || []).slice();
    const moved = arrayMove(current, oldIdx, newIdx);
    const movedIds = moved.map(s => s.id);
    // Optimistic UI: rewrite the local services array so this category appears in the new order
    // AND set sort_order so re-renders are stable. Other categories untouched.
    setServices(prev => {
      const inCat = new Map(moved.map((s, i) => [s.id, { ...s, sort_order: i }]));
      const result = [];
      let inserted = false;
      for (const s of prev) {
        if (inCat.has(s.id)) {
          if (!inserted) {
            // Insert the reordered category items in their new order at the position of the first match
            for (const id of movedIds) result.push(inCat.get(id));
            inserted = true;
          }
          // skip — already inserted via movedIds
        } else {
          result.push(s);
        }
      }
      return result;
    });
    try {
      await axios.put(`${API}/services-reorder`, { category: catName, order: movedIds }, { withCredentials: true });
    } catch {
      toast.error('Failed to save new order');
      fetchAll();
    }
  };

  const handleDragEndDnd = (catName) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const items = (grouped[catName] || []).map(s => s.id);
    const oldIdx = items.indexOf(active.id);
    const newIdx = items.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    reorderInCategory(catName, oldIdx, newIdx);
  };

  // Category reorder — dnd-kit, mouse+touch friendly
  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeId = String(active.id).replace(/^cat-/, '');
    const overId = String(over.id).replace(/^cat-/, '');
    const oldIdx = orderedCats.findIndex(c => c.id === activeId);
    const newIdx = orderedCats.findIndex(c => c.id === overId);
    if (oldIdx < 0 || newIdx < 0) return;
    const moved = arrayMove(orderedCats, oldIdx, newIdx);
    // Optimistic UI: rewrite categories to match new order
    setCategories(prev => {
      const idMap = new Map(moved.map((c, i) => [c.id, { ...c, sort_order: i }]));
      // Preserve any cats not in moved (shouldn't happen but safe)
      const result = [];
      let inserted = false;
      for (const c of prev) {
        if (idMap.has(c.id)) {
          if (!inserted) {
            for (const m of moved) result.push(idMap.get(m.id));
            inserted = true;
          }
        } else {
          result.push(c);
        }
      }
      return result;
    });
    try {
      await axios.put(`${API}/services-reorder`, { category_order: moved.map(c => c.name) }, { withCredentials: true });
    } catch {
      toast.error('Failed to save category order');
      fetchAll();
    }
  };

  // Service drag to different category OR reorder within same category
  const [dragOverIdx, setDragOverIdx] = useState({ cat: null, idx: null });
  const handleServiceDragStart = (svc) => setDragItem(svc);
  const handleServiceDragOver = (e, catName) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverCat(catName); };
  const handleItemDragOver = (e, catName, idx) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setDragOverCat(catName); setDragOverIdx({ cat: catName, idx }); };
  const handleServiceDrop = async (catName) => {
    if (!dragItem) { setDragOverIdx({ cat: null, idx: null }); return; }
    const targetCat = catName;
    const sameCat = (dragItem.category || 'Uncategorised') === targetCat;
    const idx = (dragOverIdx.cat === targetCat && dragOverIdx.idx != null) ? dragOverIdx.idx : null;
    try {
      if (sameCat && idx != null) {
        // Reorder within the same category — compute new order array of service IDs
        const current = (grouped[targetCat] || []).filter(s => s.id !== dragItem.id);
        const insertAt = Math.max(0, Math.min(idx, current.length));
        current.splice(insertAt, 0, dragItem);
        await axios.put(`${API}/services/reorder`, { category: targetCat, order: current.map(s => s.id) }, { withCredentials: true });
        fetchAll();
      } else if (!sameCat) {
        // Cross-category move — append to end of new category (or insert at idx if provided)
        const target = (grouped[targetCat] || []).filter(s => s.id !== dragItem.id);
        const insertAt = idx != null ? Math.max(0, Math.min(idx, target.length)) : target.length;
        target.splice(insertAt, 0, { ...dragItem, category: targetCat });
        await axios.put(`${API}/services/${dragItem.id}/move`, { category: targetCat }, { withCredentials: true });
        await axios.put(`${API}/services/reorder`, { category: targetCat, order: target.map(s => s.id) }, { withCredentials: true });
        fetchAll();
      }
    } catch { toast.error('Failed to reorder'); }
    setDragItem(null); setDragOverCat(null); setDragOverIdx({ cat: null, idx: null });
  };

  // Category drag reorder
  const handleCatDragStart = (cat) => setDragCat(cat);
  const handleCatDragOverIdx = (e, idx) => { e.preventDefault(); setDragOverCatIdx(idx); };
  const handleCatDrop = async (idx) => {
    if (dragCat) {
      const newOrder = categories.filter(c => c.id !== dragCat.id);
      newOrder.splice(idx, 0, dragCat);
      setCategories(newOrder);
      try {
        await axios.put(`${API}/service-categories/reorder`, { order: newOrder.map(c => c.id) }, { withCredentials: true });
      } catch { toast.error('Failed to reorder'); }
    }
    setDragCat(null); setDragOverCatIdx(null);
  };

  // Build ordered groups
  const catNames = categories.map(c => c.name);
  const grouped = {};
  services.forEach(s => {
    const cat = s.category || 'Uncategorised';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(s);
  });
  const orderedCats = [...categories];
  const extraCats = Object.keys(grouped).filter(c => !catNames.includes(c));

  const allCategoryNames = [...catNames, ...extraCats];

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/services/import-csv`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${data.imported || 0} services`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); if (csvRef.current) csvRef.current.value = ''; }
  };

  const saveFpEdit = async () => {
    if (!fpEdit) return;
    try {
      const payload = { ...form, duration: parseInt(form.duration), price: parseFloat(form.price), category: form.category === '__uncategorised__' ? '' : form.category, location_ids: fpSvcLocations };
      await axios.put(`${API}/services/${fpEdit.id}`, payload, { withCredentials: true });
      // Save team member assignments
      const selectedStaffIds = fpTeam.filter(m => m.assigned).map(m => m.id);
      const allSelected = fpTeam.every(m => m.assigned);
      await axios.put(`${API}/services/${fpEdit.id}/team-members`, { 
        staff_ids: selectedStaffIds, 
        all_selected: allSelected 
      }, { withCredentials: true });
      toast.success('Service updated');
      setFpEdit(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const toggleFpTeamMember = (staffId) => {
    setFpTeam(prev => prev.map(m => m.id === staffId ? { ...m, assigned: !m.assigned } : m));
  };
  const toggleFpAllTeam = () => {
    const allOn = fpTeam.every(m => m.assigned);
    setFpTeam(prev => prev.map(m => ({ ...m, assigned: !allOn })));
  };
  const toggleFpLocation = (locId) => {
    setFpSvcLocations(prev => prev.includes(locId) ? prev.filter(l => l !== locId) : [...prev, locId]);
  };

  // Full-page edit view (Fresha-style)
  if (fpEdit) {
    const fpTabs = [
      { key: 'basic', label: 'Basic details' },
      { key: 'locations', label: 'Locations', count: fpSvcLocations.length || null },
      { key: 'team', label: 'Team members', count: fpTeam.filter(m => m.assigned).length || null },
    ];
    return (
      <div className="fade-in" data-testid="service-fullpage-edit">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setFpEdit(null)} className="flex items-center gap-2 text-sm text-[#556B2F] hover:text-[#4A5D23]"><ArrowLeft size={16} /> Back to Services</button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setFpEdit(null)} className="border-[#E5E7E1]">Close</Button>
            <Button onClick={saveFpEdit} className="bg-[#1A2421] hover:bg-[#2D3B36] text-white" data-testid="fp-svc-save">Save</Button>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-[#1A2421] mb-6" style={{ fontFamily: 'Outfit' }}>Edit service</h1>
        <div className="flex gap-8">
          {/* Sidebar tabs */}
          <div className="w-56 flex-shrink-0">
            <nav className="space-y-1 border border-[#E5E7E1] rounded-xl p-3">
              {fpTabs.map(tab => (
                <button key={tab.key} onClick={() => setFpTab(tab.key)} data-testid={`fp-svc-tab-${tab.key}`}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${fpTab === tab.key ? 'bg-[#F5F7F0] text-[#1A2421]' : 'text-[#5C6656] hover:text-[#1A2421] hover:bg-[#F9F8F6]'}`}>
                  {tab.label}
                  {tab.count != null && <span className="text-xs text-[#5C6656] bg-[#F5F5F3] px-2 py-0.5 rounded-full">{tab.count}</span>}
                </button>
              ))}
              <div className="border-t border-[#E5E7E1] my-2 pt-2">
                <p className="text-[10px] uppercase tracking-wider text-[#5C6656] font-bold px-3 mb-1">Settings</p>
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-[#1A2421]">Active</span>
                  <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} />
                </div>
              </div>
            </nav>
          </div>
          {/* Content area */}
          <div className="flex-1 max-w-2xl">
            {fpTab === 'basic' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Basic details</h2>
                <div className="space-y-2">
                  <Label>Service name</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="border-[#E5E7E1]" data-testid="fp-svc-name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Menu category</Label>
                    <Select value={form.category || '__uncategorised__'} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                      <SelectTrigger className="border-[#E5E7E1]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {allCategoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        <SelectItem value="__uncategorised__">Uncategorised</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} className="border-[#E5E7E1]" placeholder="Add a short description" maxLength={1000} />
                  <p className="text-xs text-[#5C6656] text-right">{(form.description || '').length}/1000</p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1A2421] mb-4">Pricing and duration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Price ({'\u00A3'})</Label>
                      <Input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} className="border-[#E5E7E1]" />
                    </div>
                    <div className="space-y-2">
                      <Label>Duration (min)</Label>
                      <Input type="number" min="5" value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))} className="border-[#E5E7E1]" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {fpTab === 'locations' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Locations</h2>
                  <p className="text-sm text-[#5C6656] mt-1">Choose the locations where this service will take place</p>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${fpSvcLocations.length === 0 || fpSvcLocations.length === fpLocations.length ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#D1D5CB]'}`}
                      onClick={() => setFpSvcLocations(fpSvcLocations.length === fpLocations.length ? [] : fpLocations.map(l => l.id))}>
                      {(fpSvcLocations.length === 0 || fpSvcLocations.length === fpLocations.length) && <Check size={14} className="text-white" />}
                    </div>
                    <span className="text-sm font-semibold text-[#1A2421]">All locations</span>
                    <span className="text-xs text-[#5C6656]">{fpLocations.length}</span>
                  </label>
                  {fpLocations.map(loc => {
                    const checked = fpSvcLocations.length === 0 || fpSvcLocations.includes(loc.id);
                    return (
                      <label key={loc.id} className="flex items-center gap-3 py-3 border-b border-[#E5E7E1] cursor-pointer" onClick={() => toggleFpLocation(loc.id)}>
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${checked ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#D1D5CB]'}`}>
                          {checked && <Check size={14} className="text-white" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1A2421]">{loc.name}</p>
                          {loc.address && <p className="text-xs text-[#5C6656]">{loc.address}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {fpTab === 'team' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Team members required</h2>
                  <p className="text-sm text-[#5C6656] mt-1">Choose which team members will perform this service</p>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer" onClick={toggleFpAllTeam}>
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${fpTeam.every(m => m.assigned) ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#D1D5CB]'}`}>
                      {fpTeam.every(m => m.assigned) && <Check size={14} className="text-white" />}
                    </div>
                    <span className="text-sm font-semibold text-[#1A2421]">All team members</span>
                    <span className="text-xs text-[#5C6656]">{fpTeam.length}</span>
                  </label>
                  {fpTeam.map(member => (
                    <label key={member.id} className="flex items-center gap-3 py-3 border-b border-[#E5E7E1] cursor-pointer" onClick={() => toggleFpTeamMember(member.id)}>
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${member.assigned ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#D1D5CB]'}`}>
                        {member.assigned && <Check size={14} className="text-white" />}
                      </div>
                      <div className="w-10 h-10 rounded-full bg-[#E9EDDF] flex items-center justify-center text-sm font-bold text-[#556B2F]">
                        {member.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-[#1A2421]">{member.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in space-y-6" data-testid="services-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>{t('services.title')}</h1>
          <p className="text-sm text-[#5C6656] mt-1">{services.length} {t('services.title').toLowerCase()} in {allCategoryNames.length} {t('services.category').toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" ref={csvRef} accept=".csv" className="hidden" onChange={handleCsvImport} />
          <Button variant="outline" onClick={() => csvRef.current?.click()} disabled={importing} className="border-[#E5E7E1] text-[#556B2F]" data-testid="import-csv-btn">
            <Upload size={14} className="mr-1" /> {importing ? 'Importing...' : 'Import CSV'}
          </Button>
          <Button variant="outline" onClick={() => { setCatForm(''); setEditCatId(null); setCatDialogOpen(true); }} className="border-[#E5E7E1] text-[#556B2F]" data-testid="add-category-btn">
            <FolderPlus size={14} className="mr-1" /> {t('services.addCategory')}
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="add-service-btn">
            <Plus size={16} className="mr-1" /> {t('services.addService')}
          </Button>
        </div>
      </div>

      {services.length === 0 && categories.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#5C6656] mb-4">No services yet. Start by adding a category and your first service.</p>
          <Button onClick={() => { setCatForm(''); setEditCatId(null); setCatDialogOpen(true); }} variant="outline" className="border-[#E5E7E1]"><FolderPlus size={14} className="mr-1" /> Add your first category</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Ordered categories — dnd-kit drag */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
            <SortableContext items={orderedCats.map(c => `cat-${c.id}`)} strategy={verticalListSortingStrategy}>
              {orderedCats.map((cat) => {
                const catServices = grouped[cat.name] || [];
                const isCollapsed = collapsedCats[cat.name];
                return (
                  <SortableCategoryBlock key={cat.id} cat={cat}>
                    {({ dragAttributes, dragListeners }) => (
                      <>
                        {/* Category header */}
                        <div className="flex items-center gap-2 px-5 py-3 select-none">
                          <button type="button" {...dragAttributes} {...dragListeners}
                            className="touch-none cursor-grab active:cursor-grabbing p-1 -ml-1 text-[#B0B5A8] hover:text-[#556B2F]"
                            aria-label="Drag category">
                            <GripVertical size={16} />
                          </button>
                          <button type="button" onClick={() => toggleCollapse(cat.name)} className="flex items-center gap-2 flex-1 text-left">
                            {isCollapsed ? <ChevronRight size={16} className="text-[#5C6656]" /> : <ChevronDown size={16} className="text-[#5C6656]" />}
                            <h3 className="text-sm font-bold uppercase tracking-wide text-[#1A2421] flex-1" style={{ fontFamily: 'Outfit' }}>{cat.name}</h3>
                            <span className="text-xs text-[#5C6656]">{catServices.length} service{catServices.length !== 1 ? 's' : ''}</span>
                          </button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[#556B2F]" onClick={(e) => { e.stopPropagation(); openAddInCategory(cat.name); }}><Plus size={14} /></Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[#556B2F]" onClick={(e) => { e.stopPropagation(); setEditCatId(cat.id); setCatForm(cat.name); setCatDialogOpen(true); }}><Pencil size={12} /></Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[#E2725B]" onClick={(e) => { e.stopPropagation(); deleteCategory(cat); }}><Trash2 size={12} /></Button>
                        </div>
                        {/* Services list */}
                        {!isCollapsed && (
                          <div className="px-5 pb-3 space-y-1">
                            {catServices.length === 0 && (
                              <div className="py-6 text-center text-sm text-[#5C6656] border border-dashed border-[#E5E7E1] rounded-lg">
                                No services — click + to add one
                              </div>
                            )}
                            {catServices.length > 0 && (
                              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndDnd(cat.name)}>
                                <SortableContext items={catServices.map(s => s.id)} strategy={verticalListSortingStrategy}>
                                  <div className="space-y-1">
                                    {catServices.map(s => (
                                      <SortableServiceRow key={s.id} s={s} onEdit={openEdit} onDelete={deleteService} />
                                    ))}
                                  </div>
                                </SortableContext>
                              </DndContext>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </SortableCategoryBlock>
                );
              })}
            </SortableContext>
          </DndContext>

          {/* Extra categories (from services not in formal categories) */}
          {extraCats.map(catName => {
            const catServices = grouped[catName] || [];
            const isCollapsed = collapsedCats[catName];
            return (
              <div key={catName} className="border border-[#E5E7E1] rounded-xl bg-white" data-testid={`cat-group-${catName}`}>
                <div className="flex items-center gap-2 px-5 py-3 cursor-pointer select-none" onClick={() => toggleCollapse(catName)}>
                  {isCollapsed ? <ChevronRight size={16} className="text-[#5C6656]" /> : <ChevronDown size={16} className="text-[#5C6656]" />}
                  <h3 className="text-sm font-bold uppercase tracking-wide text-[#5C6656] flex-1" style={{ fontFamily: 'Outfit' }}>{catName}</h3>
                  <span className="text-xs text-[#5C6656]">{catServices.length}</span>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-[#556B2F]" onClick={(e) => { e.stopPropagation(); openAddInCategory(catName); }}><Plus size={14} /></Button>
                </div>
                {!isCollapsed && (
                  <div className="px-5 pb-3 space-y-1">
                    {catServices.length > 0 && (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndDnd(catName)}>
                        <SortableContext items={catServices.map(s => s.id)} strategy={verticalListSortingStrategy}>
                          <div className="space-y-1">
                            {catServices.map(s => (
                              <SortableServiceRow key={s.id} s={s} onEdit={openEdit} onDelete={deleteService} />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-md border-[#E5E7E1]">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>{editId ? 'Edit Service' : 'New Service'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-2"><Label>Service Name *</Label><Input data-testid="service-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="border-[#E5E7E1]" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea data-testid="service-desc" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} className="border-[#E5E7E1]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Duration (min)</Label><Input data-testid="service-duration" type="number" min="5" value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))} className="border-[#E5E7E1]" /></div>
              <div className="space-y-2"><Label>Price ({'\u00A3'})</Label><Input data-testid="service-price" type="number" min="0" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} className="border-[#E5E7E1]" /></div>
            </div>
            <div className="space-y-2"><Label>Category</Label>
              <Select value={form.category || '__uncategorised__'} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="border-[#E5E7E1]" data-testid="service-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {allCategoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value="__uncategorised__">{t('services.uncategorised')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between"><Label>Active</Label><Switch data-testid="service-active" checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} /></div>
            <Button data-testid="submit-service" type="submit" className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">{editId ? 'Update Service' : 'Add Service'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="sm:max-w-sm border-[#E5E7E1]">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>{editCatId ? 'Edit Category' : 'New Category'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2"><Label>Category name *</Label><Input value={catForm} onChange={e => setCatForm(e.target.value)} className="border-[#E5E7E1]" placeholder="e.g. Hair, Nails, Spa" data-testid="cat-name-input" /></div>
            <Button onClick={saveCategory} className="w-full bg-[#1A2421] hover:bg-[#2D3B36] text-white" data-testid="save-cat-btn">{editCatId ? 'Save' : 'Add Category'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
